import {WordDiffPartType} from './getWordDiff';

export const MAX_CHANGED_SEGMENT_LENGTH = 160;
export const CHANGED_EXCERPT_CONTEXT_LENGTH = 40;

export interface ChangedExcerptPart {
  value: string;
  type: WordDiffPartType;
  isTruncated: boolean;
}

export interface ChangedExcerpt {
  parts: ChangedExcerptPart[];
  hasPrecedingText: boolean;
}

const HIGH_SURROGATE_START = 0xd800;
const HIGH_SURROGATE_END = 0xdbff;
const LOW_SURROGATE_START = 0xdc00;
const LOW_SURROGATE_END = 0xdfff;

const isInsideSurrogatePair = (text: string, index: number): boolean => {
  const before = text.charCodeAt(index - 1);
  const after = text.charCodeAt(index);

  return (
    before >= HIGH_SURROGATE_START &&
    before <= HIGH_SURROGATE_END &&
    after >= LOW_SURROGATE_START &&
    after <= LOW_SURROGATE_END
  );
};

const getCommonPrefixLength = (previous: string, current: string): number => {
  const limit = Math.min(previous.length, current.length);
  let length = 0;

  while (length < limit && previous.charCodeAt(length) === current.charCodeAt(length)) {
    length++;
  }

  return isInsideSurrogatePair(previous, length) ? length - 1 : length;
};

const getCommonSuffixLength = (previous: string, current: string, limit: number): number => {
  let length = 0;

  while (
    length < limit &&
    previous.charCodeAt(previous.length - 1 - length) ===
      current.charCodeAt(current.length - 1 - length)
  ) {
    length++;
  }

  return isInsideSurrogatePair(previous, previous.length - length) ? length - 1 : length;
};

const takeHead = (text: string, maxLength: number) => {
  if (text.length <= maxLength) {
    return {value: text, isTruncated: false};
  }

  const end = isInsideSurrogatePair(text, maxLength) ? maxLength - 1 : maxLength;

  return {value: text.slice(0, end), isTruncated: true};
};

const takeTail = (text: string, maxLength: number) => {
  if (text.length <= maxLength) {
    return {value: text, hasPrecedingText: false};
  }

  const start = text.length - maxLength;
  const safeStart = isInsideSurrogatePair(text, start) ? start + 1 : start;

  return {value: text.slice(safeStart), hasPrecedingText: true};
};

const toPart = (
  value: string,
  type: WordDiffPartType,
  isTruncated: boolean
): ChangedExcerptPart[] => (value === '' ? [] : [{value, type, isTruncated}]);

export const getChangedExcerpt = (previous: string, current: string): ChangedExcerpt | null => {
  if (previous === current) {
    return null;
  }

  const prefixLength = getCommonPrefixLength(previous, current);
  const suffixLength = getCommonSuffixLength(
    previous,
    current,
    Math.min(previous.length, current.length) - prefixLength
  );

  const removed = takeHead(
    previous.slice(prefixLength, previous.length - suffixLength),
    MAX_CHANGED_SEGMENT_LENGTH
  );
  const added = takeHead(
    current.slice(prefixLength, current.length - suffixLength),
    MAX_CHANGED_SEGMENT_LENGTH
  );
  const before = takeTail(previous.slice(0, prefixLength), CHANGED_EXCERPT_CONTEXT_LENGTH);
  const after = takeHead(
    current.slice(current.length - suffixLength),
    CHANGED_EXCERPT_CONTEXT_LENGTH
  );

  return {
    parts: [
      ...toPart(before.value, 'common', false),
      ...toPart(removed.value, 'removed', removed.isTruncated),
      ...toPart(added.value, 'added', added.isTruncated),
      ...toPart(after.value, 'common', after.isTruncated),
    ],
    hasPrecedingText: before.hasPrecedingText,
  };
};
